import styled from "styled-components";
import BreadCrumbs from "../../../components/ui-v2/BreadCrumbs";
import { ROUTES } from "../../../router";
import Search from "../../../components/ui-v2/Inputs/Search";
import DropdownFilter from "../../../components/ui-v2/DropdownFilter";
import {
  /* Button, */ TWithIconVariants,
} from "../../../components/ui-v2/Button";
import {
  TCellButton,
  /*  Table, */ TCellStyleVariants,
  TTableBody,
} from "../../../components/ui-v2/TableGrid";
import Modal from "../../../components/ui-v2/Modal";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  TTagColorVariants,
  TTagWithIconVariants,
} from "../../../components/ui-v2/Tag";
import { setToastState } from "../../../redux/reducers/globalState";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { TOrganization } from "../../../redux/reducers/organizations";
import { getQuerystringParams } from "../../../utils/miscellaneous";
import { useNavigationManager } from "../../../hooks/useNavigationManager";
import { cnpjMask } from "../../../utils/formatters";
import TableGrid from "../../../components/ui-v2/TableGrid";
import axios, { CancelTokenSource } from "axios";
import SetDefaultGoodsStockMenu from "../../../components/ui-v2/Menus/SetDefaultGoodsStockMenu";
import {
  filterOrganizationByVisibilityThunk,
  toggleOrganizationVisibilityThunk,
} from "../../../redux/actions";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import { selectedOrganization } from "../../../redux/selectors/organizations";
import { useProportionalGridTemplateColumns } from "../../../hooks/useProportionalGridTemplateColumns";

const Container = styled.main`
  position: relative;

  & > h1 {
    font-family: var(--font-title-bold);
    font-size: 28px;
    color: var(--color-text-title);
    margin-top: 10px;
  }

  & > *:nth-child(1) {
    margin-top: 20px;
  }

  tr {
    td:nth-child(8) {
      width: 10px;
    }
    td:nth-child(9) {
      button {
        border-color: var(--color-aux-danger-700);
        svg {
          color: var(--color-aux-danger-700);
        }
      }
    }
  }
`;

const ContentContainer = styled.div`
  margin-top: 20px;

  & > *:not(& > *:first-child) {
    margin-left: 16px;
  }

  display: flex;
  align-items: center;
`;

const Organizations: React.FC = () => {
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const [searchKey, setSearchKey] = useState(0);

  const { data: organizationData, loading } = useTypedSelector(
    (state) => state.settingsOrganizations
  );

  const reduxQueryStrings = useTypedSelector((state) => state.querystring);

  const sidebarState = useTypedSelector((state) => state.sidebar);

  const selectOrganization = useTypedSelector(selectedOrganization());

  const { setQuerystring } = useNavigationManager();

  const pageRef = useRef<number | undefined>();

  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [organizationToUpdate, setOrganizationToUpdate] =
    useState<TOrganization | null>(null);

  const [forceSelect, setForceSelect] = useState("");

  const [anchorDefaultGoodsStockMenu, setAnchorDefaultGoodsStockMenu] =
    useState<HTMLElement | null>(null);

  const componentRef = useRef(anchorDefaultGoodsStockMenu);

  const dispatch = useTypedDispatch();

  // 119px 0px 709px 709px 600px 119px
  const originalWidths = [119, 0, 709, 709, 600, 119];

  const sidebarOffset = sidebarState.isFixed ? 240 : 48;

  const gridTemplateColumns =
    useProportionalGridTemplateColumns(originalWidths);

  const TOAST_MESSAGES = [
    "A visibilidade da Organização foi alterada",
    "Não foi possível alterar a visibilidade da Organização, tente novamente",
  ];

  const visibleCell = {
    value: "Visível",
    style: "green" as TTagColorVariants,
    icon: "eye" as TTagWithIconVariants,
    iconPosition: "left" as "left" | "right",
  };

  const notVisibleCell = {
    value: "Oculta",
    style: "red" as TTagColorVariants,
    icon: "eyeSlash" as TTagWithIconVariants,
    iconPosition: "left" as "left" | "right",
  };

  const onClickHandler = (organization: TOrganization) => {
    setOrganizationToUpdate(organization);
    setShouldOpenModal(true);
  };

  const modalOnSuccessHandler = () => {
    if (organizationToUpdate) {
      const querystring = { ...reduxQueryStrings };
      if (querystring.visibility) {
        delete querystring.visibility;
      }

      dispatch(
        toggleOrganizationVisibilityThunk({
          organizationId: organizationToUpdate.uuid,
          isActive: organizationToUpdate.is_active,
          cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
        })
      )
        .unwrap()
        .then(() => {
          setShouldOpenModal(false);
          handleOnClear();

          dispatch(
            setToastState({
              message: { title: "Sucesso", description: TOAST_MESSAGES[0] },
              shouldShow: true,
              variant: "success",
            })
          );
        })
        .catch(() => {
          setShouldOpenModal(false);
          dispatch(
            setToastState({
              message: { title: "Erro", description: TOAST_MESSAGES[1] },
              shouldShow: true,
              variant: "error",
            })
          );
        });
    }
  };

  const handleVisibilityFilter = () => {
    dispatch(
      filterOrganizationByVisibilityThunk({
        cancelToken: (cancelTokenSource.current as CancelTokenSource).token,
      })
    );
  };

  const modalOnCancelHandler = () => {
    setShouldOpenModal(false);
  };

  const modalOnCloseHandler = () => {
    setShouldOpenModal(false);
  };

  const getOrganizations = useCallback(async () => {
    cancelTokenSource.current = axios.CancelToken.source();
    handleFetchDataByQueryParams();
  }, [reduxQueryStrings.visibility]);

  const handleNavigate = (select: string) => {
    const querystring = { ...reduxQueryStrings };

    switch (select) {
      case "Todos":
        delete querystring.visibility;
        break;
      case "Vis\u00EDvel":
        querystring.visibility = "visible";
        break;
      case "Oculta":
        querystring.visibility = "hidden";
        break;
    }
    querystring.page = 1;
    setQuerystring(querystring);

    handleFetchDataByQueryParams();
  };

  const handleOnClear = () => {
    const querystrings = { ...reduxQueryStrings };

    delete querystrings.orgs_search;

    setSearchKey((prev) => prev + 1); // force remount

    setQuerystring(querystrings);
    handleFetchDataByQueryParams();
  };

  const handleOnSearch = (searchPhrase: string) => {
    const querystrings = { ...reduxQueryStrings };

    if (searchPhrase.length > 0) querystrings.orgs_search = searchPhrase;
    else delete querystrings.orgs_search;
    querystrings.page = 1;

    setQuerystring(querystrings);

    handleFetchDataByQueryParams();
  };

  const handleFetchDataByQueryParams = () => {
    const querystring = { ...reduxQueryStrings };
    if (querystring.visibility) {
      if (querystring.visibility === "visible") {
        setForceSelect("Visível");
      } else if (querystring.visibility === "hidden") {
        setForceSelect("Oculta");
      } else {
        setForceSelect("");
      }
      handleVisibilityFilter();
    } else {
      handleVisibilityFilter();
      setForceSelect("");
    }
  };

  useEffect(() => {
    getOrganizations();
  }, [getOrganizations]);

  useEffect(() => {
    return () => {
      const querystring = getQuerystringParams();
      querystring.page = 1;
      delete querystring.orgs_search;
      delete querystring.visibility;
      delete querystring.status;
      delete querystring.role;
      setQuerystring(querystring);

      if (cancelTokenSource.current) cancelTokenSource.current.cancel();
    };
  }, []);

  return (
    <Container>
      <BreadCrumbs
        names={["Início", "Configurações", "Organizações"]}
        urls={[ROUTES.HOME, ROUTES.SETTINGS, ROUTES.SETTINGS_ORGANIZATIONS]}
      />
      <h1>Gerenciar organizações</h1>
      {/* <SyncButtonArea>
        <Button
          variant="outline"
          icon="sync"
          iconPosition="left"
          text={isAccountSyncing ? "Sincronizando" : "Sincronizar Organizações"}
          onClick={handleSyncOrganizations}
          isLoading={isAccountSyncing}
          disabled={isAccountSyncing}
        />
        <p>
          {selectAccount && !isAccountSyncing && <>{handleSyncMessage()}</>}
        </p>
      </SyncButtonArea> */}
      <ContentContainer>
        <Search
          key={searchKey}
          onClear={handleOnClear}
          $ref={(ref, setSearchString) => {
            const queryStrings = getQuerystringParams();
            if (queryStrings.orgs_search && ref.current) {
              const search = decodeURIComponent(
                queryStrings.orgs_search
              ); /* .replace(
                /\+/g,
                " "
              ) */
              ref.current.value = search;
              setSearchString(search);
            }
          }}
          variant="default"
          placeholder="Buscar Organizações"
          onSearch={handleOnSearch}
        />
        <DropdownFilter
          menuVariant="unlabeled"
          buttonVariant="eye"
          placeholder="Visibilidade"
          search={false}
          content={["Todos", "Visível", "Oculta"]}
          onSelect={handleNavigate}
          isSingleSelect
          forceSelectOptionsByIndex={[
            ["Todos", "Visível", "Oculta"].indexOf(forceSelect),
          ]}
          filled={
            ["Todos", "Visível", "Oculta"].includes(forceSelect)
              ? [forceSelect]
              : undefined
          }
          key={forceSelect.toString()}
        />
      </ContentContainer>

      <TableGrid
        key={organizationData && organizationData.count}
        columns={{
          tableHead: {
            width: [
              // 119px 0px 709px 709px 600px 119px
              gridTemplateColumns,
            ],
          },
          tableBody: {
            width: [gridTemplateColumns],
          },
        }}
        selectType={{ type: "noSelect" }}
        tableHead={{
          customPaddingTop: "0px",
          value: [
            { value: "Nome", style: "noIcon", showSkeleton: loading },
            { value: "CNPJ", style: "noIcon", showSkeleton: loading },
            {
              value: "Visibilidade",
              style: "icon",
              icon: "infoCircle",
              iconPosition: "right",
              iconColor: "--color-text-labels",
              tooltip: {
                text: [
                  {
                    style: "default",
                    text: "A visibilidade é a permissão que todos",
                    shouldBreakLine: true,
                  },
                  {
                    style: "default",
                    text: "os usuários do portal têm para acessar",
                    shouldBreakLine: true,
                  },
                  {
                    style: "default",
                    text: "esta Organização",
                    shouldBreakLine: true,
                  },
                ],
                size: "large",
              },
              showSkeleton: loading,
            },
            { value: "Ações", style: "noIcon", showSkeleton: loading },
          ],
        }}
        isLoading={loading}
        row="singleRow"
        emptyState={{
          isEmptyState:
            organizationData !== null && organizationData?.count === 0,
          title: "Nenhum resultado encontrado",
          description: "Altere os filtros ou sincronize as Organizações",
        }}
        tableBody={
          loading
            ? new Array(5).fill("").map(() => ({
                elementId: "",
                file_nfe: null,
                file_xml: null,
                body: [
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "200px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "150px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "88px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      containerCss: {
                        display: "flex",
                        gap: "2px",
                        width: "80px",
                        overflow: "hidden",
                      },
                      values: [{ width: "32px" }, { width: "32px" }],
                    },
                  },
                ] as TTableBody<TCellStyleVariants>[],
              }))
            : (organizationData?.results ?? []).map((organization, index) => ({
                elementId: "",
                file_nfe: null,
                file_xml: null,
                body: [
                  { value: organization.company_name, style: "default" },
                  { value: cnpjMask(organization.cnpj), style: "default" },

                  organization.is_active ? visibleCell : notVisibleCell,
                  [
                    {
                      style: "button",
                      icon: organization.is_active ? "eyeSlash" : "eye",
                      onClick() {
                        onClickHandler(organization);
                      },
                      tooltip: {
                        text: [
                          {
                            text: "Alterar visibilidade",
                            shouldBreakLine: false,
                            style: "default",
                          },
                        ],
                      },
                    } as TCellButton<TWithIconVariants>,
                    {
                      style: "button",
                      icon: "import",
                      id: "import" + index,
                      onClick(e: React.MouseEvent<HTMLElement>) {
                        if (anchorDefaultGoodsStockMenu) {
                          componentRef.current = null;
                          setAnchorDefaultGoodsStockMenu(null);
                        } else {
                          componentRef.current = e.currentTarget;
                          setAnchorDefaultGoodsStockMenu(e.currentTarget);
                        }
                      },
                      tooltip: {
                        text: [
                          {
                            text: "Alterar estoque padrão",
                            shouldBreakLine: false,
                            style: "default",
                          },
                        ],
                      },
                    } as TCellButton<TWithIconVariants>,
                  ],
                ] as TTableBody<TCellStyleVariants>[],
              }))
        }
        footer={{
          customStyle: {
            width: `calc(100vw - 48px - ${sidebarOffset}px)`,
          },
          customPagination: organizationData
            ? {
                fixedBottom: {
                  isFixedAtTheBottom: true,
                  customBottomPosition: "0px",
                },
                currentPage: `${organizationData?.current_page}`,
                buttonBackward: {
                  disabled: organizationData.current_page === 1,
                  onClick() {
                    if (organizationData.current_page === 1) {
                      pageRef.current = undefined;
                    } else {
                      pageRef.current = organizationData.current_page - 1;
                    }

                    if (organizationData.current_page !== 1) {
                      const querystring = { ...reduxQueryStrings };
                      if (pageRef.current) querystring.page = pageRef.current;

                      setQuerystring(querystring);
                      dispatch(
                        filterOrganizationByVisibilityThunk({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    }
                  },
                },
                buttonForward: {
                  disabled:
                    organizationData.current_page ===
                    organizationData.pages.length,
                  onClick() {
                    if (
                      organizationData.current_page ===
                      organizationData.pages.length
                    ) {
                      pageRef.current = organizationData.current_page;
                    } else {
                      pageRef.current = organizationData.current_page + 1;
                    }

                    if (
                      organizationData.current_page !==
                      organizationData.pages.length
                    ) {
                      const querystring = { ...reduxQueryStrings };
                      if (pageRef.current) querystring.page = pageRef.current;

                      setQuerystring(querystring);
                      dispatch(
                        filterOrganizationByVisibilityThunk({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    }
                  },
                },
                pages: organizationData.pages.map((page) => {
                  return {
                    value: Object.keys(page)[0],
                    onClick() {
                      // Always set pageRef.current to the correct page number
                      const pageNumber = parseInt(Object.keys(page)[0]);
                      pageRef.current = pageNumber;

                      const querystring = { ...reduxQueryStrings };
                      querystring.page = pageNumber;
                      setQuerystring(querystring);
                      dispatch(
                        filterOrganizationByVisibilityThunk({
                          cancelToken: (
                            cancelTokenSource.current as CancelTokenSource
                          ).token,
                        })
                      );
                    },
                  };
                }),
              }
            : undefined,
        }}
      />
      <Modal
        title="Alterar visibilidade"
        icon={{ type: "exclamationTriangle", color: "orange" }}
        main={{
          style: "textOnly",
          content: [
            {
              text: organizationToUpdate?.is_active
                ? "Tem certeza que deseja ocultar a Organização "
                : "Tem certeza que deseja deixar visível a Organização ",
              style: "default",
              shouldBreakLine: false,
            },
            {
              text: organizationToUpdate?.company_name ?? "",
              style: "bold",
              shouldBreakLine: false,
            },
            { text: "?", style: "default", shouldBreakLine: true },
            { text: "", style: "default", shouldBreakLine: true },
            {
              text: "Os usuários que têm acesso a ela ",
              style: "default",
              shouldBreakLine: false,
            },
            organizationToUpdate?.is_active
              ? {
                  text: "não",
                  style: "bold",
                  shouldBreakLine: false,
                }
              : {
                  text: "",
                  style: "default",
                  shouldBreakLine: false,
                },
            organizationToUpdate?.is_active
              ? {
                  text: " poderão mais acessá-la.",
                  style: "default",
                  shouldBreakLine: true,
                }
              : {
                  text: " poderão visualizá-la novamente.",
                  style: "default",
                  shouldBreakLine: true,
                },
          ],
        }}
        footer={{
          style: "default",
          buttons: [
            {
              variant: "secondary",
              icon: "noIcon",
              text: "Cancelar",
              onClick() {
                modalOnCancelHandler();
              },
            },
            {
              variant: "orange",
              iconPosition: "left",
              icon: "checkmark",
              text: "Sim, alterar",
              onClick() {
                modalOnSuccessHandler();
              },
            },
          ],
        }}
        shouldOpen={shouldOpenModal}
        onClose={modalOnCloseHandler}
      />
      {anchorDefaultGoodsStockMenu && selectOrganization && (
        <SetDefaultGoodsStockMenu
          onCancel={() => {
            componentRef.current = null;
            setAnchorDefaultGoodsStockMenu(null);
          }}
          selectOrganization={selectOrganization}
          style={{
            position: "absolute",
            top: anchorDefaultGoodsStockMenu.getBoundingClientRect().top - 60,
            left:
              anchorDefaultGoodsStockMenu.getBoundingClientRect().left -
              380 -
              15,
          }}
        />
      )}
    </Container>
  );
};

export default Organizations;
